import { Fragment, useMemo, useState } from "react";
import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  createColumnHelper,
  SortingState,
} from "@tanstack/react-table";
import { IHiddenPage, WidgetPage } from "../../models/HiddenPages";

const colHelper = createColumnHelper<IHiddenPage>();

function widgetPageToLabel(page: WidgetPage) {
  // If WidgetPage is a numeric TS enum, this works nicely:
  return WidgetPage[page] ?? `${page}`;
}

export default function HiddenPagesTable({
  hiddenPages,
  setHiddenPage,
}: {
  hiddenPages: IHiddenPage[];
  setHiddenPage: (hp: IHiddenPage) => void;
}) {
  const [sorting, setSorting] = useState<SortingState>([]);

  const cols = useMemo(
    () => [
      colHelper.display({
        id: "page",
        header: "Page",
        cell: (info) => <span>{widgetPageToLabel(info.row.original.page)}</span>,
      }),
      colHelper.accessor("isHidden", {
        header: "Hidden?",
        cell: (info) => (
          <span className={info.getValue() ? "font-semibold text-red-700" : "text-green-700"}>
            {info.getValue() ? "Yes" : "No"}
          </span>
        ),
      }),
      colHelper.display({
        id: "defaults",
        header: "Default Values",
        cell: (info) => {
          const txt = info.row.original.defaultValuesJson?.trim() ?? "";
          if (!txt) return <span className="text-gray-400">None</span>;
          return <span className="text-gray-700">Set</span>;
        },
      }),
    ],
    []
  );

  const table = useReactTable({
    data: hiddenPages,
    columns: cols,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return (
    <div className="mt-4 bg-white overflow-auto">
      <table className="min-w-full overflow-scroll divide-y border divide-gray-300">
        <thead>
          {table.getHeaderGroups().map((hg) => (
            <tr key={hg.id} className="bg-gp-blue-50 text-gray-800">
              {hg.headers.map((header) => (
                <th
                  key={header.id}
                  scope="col"
                  className="py-3.5 px-3 text-sm font-semibold lg:table-cell text-left"
                >
                  {header.isPlaceholder ? null : (
                    <div
                      className={header.column.getCanSort() ? "cursor-pointer select-none" : ""}
                      onClick={header.column.getToggleSortingHandler()}
                    >
                      {flexRender(header.column.columnDef.header, header.getContext())}
                      {{
                        asc: " ▲",
                        desc: " ▼",
                      }[header.column.getIsSorted() as string] ?? null}
                    </div>
                  )}
                </th>
              ))}
            </tr>
          ))}
        </thead>

        <tbody>
          {table.getRowModel().rows.map((row, i) => (
            <Fragment key={i}>
              <tr
                key={row.id}
                onClick={() => setHiddenPage(row.original)}
                className="hover:bg-gray-100 hover:cursor-pointer"
              >
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id} className="px-3 py-3 border text-sm">
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            </Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}
